import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import {
  useDashboardMetricGridContext,
  type DashboardMetricVariant,
} from '@/components/dashboard/dashboard-metric-grid-context'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

export type { DashboardMetricVariant }

export type DashboardMetricCardProps = {
  title: string
  value: ReactNode
  description?: string
  icon?: LucideIcon
  loading?: boolean
  /** Override the parent grid. `plain` is label + value on the page background. */
  variant?: DashboardMetricVariant
  className?: string
}

export function DashboardMetricCard({
  title,
  value,
  description,
  icon: Icon,
  loading = false,
  variant: variantProp,
  className,
}: DashboardMetricCardProps) {
  const { showIcons, variant: gridVariant } = useDashboardMetricGridContext()
  const variant = variantProp ?? gridVariant
  const IconComponent = showIcons ? Icon : undefined
  const isPlain = variant === 'plain'

  if (loading) {
    if (isPlain) {
      return (
        <div className={cn('flex min-w-0 flex-col gap-1', className)} aria-busy="true">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-8 w-12" />
          {description ? <Skeleton className="h-3 w-16" /> : null}
        </div>
      )
    }
    return (
      <Card className={cn('h-full', className)} aria-busy="true">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <Skeleton className="h-4 w-24" />
          {IconComponent ? <Skeleton className="h-4 w-4 shrink-0 rounded" /> : null}
        </CardHeader>
        <CardContent>
          <Skeleton className="h-8 w-16" />
          {description ? <Skeleton className="mt-2 h-3 w-20" /> : null}
        </CardContent>
      </Card>
    )
  }

  if (isPlain) {
    return (
      <div className={cn('flex min-w-0 flex-col gap-1', className)}>
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          {IconComponent ? (
            <IconComponent className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          ) : null}
        </div>
        <p className="text-2xl font-bold tabular-nums">{value}</p>
        {description ? (
          <p className="text-xs text-muted-foreground">{description}</p>
        ) : null}
      </div>
    )
  }

  return (
    <Card className={cn('h-full', className)}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {IconComponent ? (
          <IconComponent className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        ) : null}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold tabular-nums">{value}</div>
        {description ? (
          <p className="text-xs text-muted-foreground">{description}</p>
        ) : null}
      </CardContent>
    </Card>
  )
}
